import { BotonExportar } from '@/components/dominio/BotonExportar';
import { MontoCRC } from '@/components/dominio/MontoCRC';
import { SortableHeader, useSortColumn } from '@/components/dominio/SortableHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useObras } from '@/hooks/useUtilidades';
import { cn } from '@/lib/utils';
import type { FilaObra } from '@adelante/shared';
import { Loader2 } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

// Umbrales para detección visual de valores atípicos.
// Validados con el user; ajustables aquí cuando se calibren.
const PCT_UTIL_BAJO = 5;
const PCT_UTIL_ALTO = 35;
const PCT_INDIR_BAJO = 15;
const PCT_INDIR_ALTO = 50;

function formatearPct(v: number | null): string {
  return v == null ? '—' : `${v.toFixed(2)}%`;
}

function celdaPctClass(valor: number | null, bajo: number, alto: number): string {
  if (valor == null) return 'text-muted-foreground';
  if (valor < bajo) return 'text-red-600 font-semibold';
  if (valor > alto) return 'text-amber-600 font-semibold';
  return '';
}

function KpiConteo({
  titulo,
  valor,
  subtitulo,
}: { titulo: string; valor: number; subtitulo?: string }) {
  return (
    <Card>
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
          {titulo}
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-hidden px-4 pb-4">
        <span className="block truncate text-xl font-semibold tabular-nums">
          {valor.toLocaleString('es-CR')}
        </span>
        {subtitulo && <p className="mt-1 text-[11px] text-muted-foreground">{subtitulo}</p>}
      </CardContent>
    </Card>
  );
}

type FuenteFiltro = 'todas' | 'BC' | 'Casos';

export function ObrasPantalla() {
  const { data, isLoading, error } = useObras();
  const refContenido = useRef<HTMLDivElement>(null);

  const [busqueda, setBusqueda] = useState('');
  const [proyectoFiltro, setProyectoFiltro] = useState<string>('todos');
  const [fuenteFiltro, setFuenteFiltro] = useState<FuenteFiltro>('todas');
  const sort = useSortColumn<FilaObra>('codigo_lote');

  // Lista de proyectos para el dropdown
  const proyectos = useMemo(() => {
    if (!data) return [];
    const set = new Set<string>();
    for (const o of data.obras) {
      if (o.abreviatura_proyecto) set.add(o.abreviatura_proyecto);
    }
    return Array.from(set).sort();
  }, [data]);

  const obrasFiltradas = useMemo<FilaObra[]>(() => {
    if (!data) return [];
    const q = busqueda.trim().toLowerCase();
    return data.obras.filter((o) => {
      if (proyectoFiltro !== 'todos' && o.abreviatura_proyecto !== proyectoFiltro) return false;
      if (fuenteFiltro !== 'todas' && o.fuente !== fuenteFiltro) return false;
      if (q) {
        const hay = `${o.codigo_lote ?? ''} ${o.cliente ?? ''} ${o.works_no ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, busqueda, proyectoFiltro, fuenteFiltro]);

  const obrasOrdenadas = useMemo(
    () => sort.sortData(obrasFiltradas),
    [obrasFiltradas, sort.sortData],
  );

  // Conteo de outliers (visibles tras filtros)
  const outliers = useMemo(() => {
    return obrasFiltradas.filter(
      (o) =>
        (o.pct_utilidad != null &&
          (o.pct_utilidad < PCT_UTIL_BAJO || o.pct_utilidad > PCT_UTIL_ALTO)) ||
        (o.pct_indirecto != null &&
          (o.pct_indirecto < PCT_INDIR_BAJO || o.pct_indirecto > PCT_INDIR_ALTO)),
    ).length;
  }, [obrasFiltradas]);

  return (
    <div ref={refContenido} className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Obras</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Presupuesto por lote — % Utilidad e Indirectos para detectar valores atípicos.
          </p>
        </div>
        <div data-pdf-ignore>
          <BotonExportar
            titulo="Obras"
            subtitulo={`${obrasFiltradas.length} obras`}
            refContenido={refContenido}
            disabled={!data}
            tablas={
              data
                ? [
                    {
                      nombre: 'Obras',
                      encabezados: [
                        'Proyecto',
                        'Lote',
                        'Cliente',
                        'works_no (BC)',
                        'Fuente',
                        'Presup. Directo',
                        'Presup. Indirecto',
                        'Utilidad Proyectada',
                        'Venta',
                        '% Util',
                        '% Indir',
                      ],
                      filas: obrasOrdenadas.map((o) => [
                        o.abreviatura_proyecto ?? '',
                        o.codigo_lote ?? '',
                        o.cliente ?? '',
                        o.works_no ?? '',
                        o.fuente,
                        o.presupuesto_directo,
                        o.presupuesto_indirecto,
                        o.utilidad_proyectada,
                        o.venta,
                        o.pct_utilidad ?? '',
                        o.pct_indirecto ?? '',
                      ]),
                    },
                  ]
                : []
            }
          />
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Cargando…
        </div>
      )}

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6 text-destructive">{error.message}</CardContent>
        </Card>
      )}

      {data && (
        <>
          {/* KPIs (conteos, no monedas — usamos Card directo en lugar de KpiCard) */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <KpiConteo titulo="Total Obras" valor={data.totales.total_obras} />
            <KpiConteo
              titulo="Con BC"
              valor={data.totales.obras_bc}
              subtitulo="Primario (BC nightly)"
            />
            <KpiConteo
              titulo="Con fallback"
              valor={data.totales.obras_casos}
              subtitulo="dbo.Casos histórico"
            />
            <Card className={outliers > 0 ? 'border-amber-400' : ''}>
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                  Valores atípicos
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-hidden px-4 pb-4">
                <span
                  className={cn(
                    'block truncate text-xl font-semibold',
                    outliers > 0 ? 'text-amber-600' : 'text-emerald-600',
                  )}
                >
                  {outliers}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {`%Util ∉ [${PCT_UTIL_BAJO}, ${PCT_UTIL_ALTO}] o %Indir ∉ [${PCT_INDIR_BAJO}, ${PCT_INDIR_ALTO}]`}
                </span>
              </CardContent>
            </Card>
          </div>

          {/* Filtros */}
          <Card>
            <CardContent className="flex flex-wrap items-center gap-3 pt-4">
              <Input
                placeholder="Buscar por lote, cliente o works_no…"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="w-64"
              />
              <Select value={proyectoFiltro} onValueChange={setProyectoFiltro}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Proyecto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los proyectos</SelectItem>
                  {proyectos.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={fuenteFiltro}
                onValueChange={(v) => setFuenteFiltro(v as FuenteFiltro)}
              >
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Fuente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas las fuentes</SelectItem>
                  <SelectItem value="BC">BC (primario)</SelectItem>
                  <SelectItem value="Casos">Casos (fallback)</SelectItem>
                </SelectContent>
              </Select>
              <span className="ml-auto text-sm text-muted-foreground">
                {obrasFiltradas.length} de {data.totales.total_obras} obras
              </span>
            </CardContent>
          </Card>

          {/* Tabla */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Presupuesto por obra</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[600px] overflow-y-auto px-6 pb-4">
                <Table>
                  <TableHeader className="sticky top-0 bg-card z-10">
                    <TableRow>
                      <TableHead>
                        <SortableHeader
                          label="Proyecto"
                          sortKey="abreviatura_proyecto"
                          activeSortKey={sort.sortKey}
                          direction={sort.direction}
                          onSort={sort.handleSort}
                        />
                      </TableHead>
                      <TableHead>
                        <SortableHeader
                          label="Lote"
                          sortKey="codigo_lote"
                          activeSortKey={sort.sortKey}
                          direction={sort.direction}
                          onSort={sort.handleSort}
                        />
                      </TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Fuente</TableHead>
                      <TableHead className="text-right">
                        <SortableHeader
                          label="Presup. Directo"
                          sortKey="presupuesto_directo"
                          activeSortKey={sort.sortKey}
                          direction={sort.direction}
                          onSort={sort.handleSort}
                          align="right"
                        />
                      </TableHead>
                      <TableHead className="text-right">
                        <SortableHeader
                          label="Presup. Indirecto"
                          sortKey="presupuesto_indirecto"
                          activeSortKey={sort.sortKey}
                          direction={sort.direction}
                          onSort={sort.handleSort}
                          align="right"
                        />
                      </TableHead>
                      <TableHead className="text-right">
                        <SortableHeader
                          label="Utilidad Proy."
                          sortKey="utilidad_proyectada"
                          activeSortKey={sort.sortKey}
                          direction={sort.direction}
                          onSort={sort.handleSort}
                          align="right"
                        />
                      </TableHead>
                      <TableHead className="text-right">
                        <SortableHeader
                          label="Venta"
                          sortKey="venta"
                          activeSortKey={sort.sortKey}
                          direction={sort.direction}
                          onSort={sort.handleSort}
                          align="right"
                        />
                      </TableHead>
                      <TableHead className="text-right">
                        <SortableHeader
                          label="% Util"
                          sortKey="pct_utilidad"
                          activeSortKey={sort.sortKey}
                          direction={sort.direction}
                          onSort={sort.handleSort}
                          align="right"
                        />
                      </TableHead>
                      <TableHead className="text-right">
                        <SortableHeader
                          label="% Indir"
                          sortKey="pct_indirecto"
                          activeSortKey={sort.sortKey}
                          direction={sort.direction}
                          onSort={sort.handleSort}
                          align="right"
                        />
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {obrasOrdenadas.map((o) => (
                      <TableRow key={o.id_lote}>
                        <TableCell className="text-xs">{o.abreviatura_proyecto ?? '—'}</TableCell>
                        <TableCell className="font-medium">{o.codigo_lote ?? '—'}</TableCell>
                        <TableCell
                          className="max-w-[180px] truncate text-xs text-muted-foreground"
                          title={o.cliente ?? undefined}
                        >
                          {o.cliente ?? '—'}
                        </TableCell>
                        <TableCell>
                          <span
                            className={cn(
                              'inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
                              o.fuente === 'BC'
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-zinc-100 text-zinc-600',
                            )}
                            title={
                              o.fuente === 'BC'
                                ? `BC works_no: ${o.works_no ?? '?'}`
                                : 'Fallback desde dbo.Casos (no hay match en BC)'
                            }
                          >
                            {o.fuente}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <MontoCRC monto={o.presupuesto_directo} decimales={false} />
                        </TableCell>
                        <TableCell className="text-right">
                          <MontoCRC monto={o.presupuesto_indirecto} decimales={false} />
                        </TableCell>
                        <TableCell className="text-right">
                          <MontoCRC monto={o.utilidad_proyectada} decimales={false} />
                        </TableCell>
                        <TableCell className="text-right">
                          <MontoCRC monto={o.venta} decimales={false} />
                        </TableCell>
                        <TableCell
                          className={cn(
                            'text-right tabular-nums',
                            celdaPctClass(o.pct_utilidad, PCT_UTIL_BAJO, PCT_UTIL_ALTO),
                          )}
                        >
                          {formatearPct(o.pct_utilidad)}
                        </TableCell>
                        <TableCell
                          className={cn(
                            'text-right tabular-nums',
                            celdaPctClass(o.pct_indirecto, PCT_INDIR_BAJO, PCT_INDIR_ALTO),
                          )}
                        >
                          {formatearPct(o.pct_indirecto)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {obrasOrdenadas.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center text-muted-foreground">
                          Sin obras que cumplan los filtros
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
